/**
 * Module where we define the semantics of applying and reverting action records.
 * Whether applying or reverting action records, the document always "moves forward" in time.
 * When we apply action records, the assumption is that the document is receiving data from some
 * outside source, and we're evolving the document forward.
 *
 * When we're reverting, we are traveling backwards in history.  However, as noted above, documents
 * always move forward.  To accomplish this, we simulate moving backwards, capture the necessary changes,
 * and then actually move forwards.
 */
import { isLegacyActionRecord, isPatchBasedActionRecord, } from "@buildwithflux/models";
import { applyChange } from "deep-diff";
import { apply, isDraft } from "mutative";
import { getImmerPatches } from "../../../history/patch/immerPatchSequence";
import { applyPatchesFast } from "./applyPatchesFast";
/**
 * Apply a forward-going deep diff to a document.  These come from legacy action records.
 * @remarks This function updates the history of the document in a slightly special way.  Because we're applying a remote
 * action record, we just bring the document "up-to-date".  In other words, it does not actually advance the history
 * of the document past the action record being applied.  We still create revert patches, but the latest action record
 * uid will match the applied record.
 *
 * QUESTION: should we also try to set latestActionRecordUid here as in applyPatchFromActionRecord?
 *
 * @deprecated use applyPatchFromActionRecord instead
 */
export function applyDeepDiffFromActionRecord(doc, deepDiff) {
    for (const diff of Object.values(deepDiff)) {
        // NOTE: In previous versions of this function, it was necessary to convert the diff in the
        // action record to an "unflattened" representation.  This is no longer needed because that work
        // is delegated to the storage layer, so all objects are unflattened once they are in memory.
        applyChange(doc, true, diff);
    }
    return doc;
}
/**
 * Apply a forward-going patch to a document.
 *
 * This function applies patches directly to the passed-in doc object, and then
 * returns it. The returned object is NOT a new reference.
 *
 * NOTE: You may want to update latestActionRecordUid and latestPatch after
 * calling this function.
 *
 * @see withHistoryUpdate for similar logic
 * @see applyPatchForward for similar logic
 */
export function applyPatchFromActionRecord(doc, patch) {
    const { forward } = getImmerPatches(patch);
    try {
        apply(doc, forward);
    }
    catch (error) {
        if (error instanceof Error && isDraft(doc)) {
            applyPatchesFast(doc, forward);
        }
        else {
            throw error;
        }
    }
    return doc;
}
/**
 * Apply a collection of forward-going action records (in the provided sequence)
 * to a document.
 */
export function applyActionRecordsToDoc(doc, actionRecords) {
    if (!isDraft(doc)) {
        // NOTE: This is important to guarantee undo/redo is synced to remote session correctly
        // See PR: https://github.com/buildwithflux/flux-app/pull/3831
        throw new Error("Please pass in a draft document to applyActionRecordsToDoc, so that applying patches doesn't replace the whole document state and result in broken patches");
    }
    actionRecords.reduce((accumulatedDoc, actionRecord) => {
        if (isPatchBasedActionRecord(actionRecord))
            return applyPatchFromActionRecord(accumulatedDoc, actionRecord.actionChangeDiff);
        else if (isLegacyActionRecord(actionRecord))
            return applyDeepDiffFromActionRecord(accumulatedDoc, actionRecord.actionChangeDiff);
        // This is dumb and defensive, but because the IActionRecord type isn't a real discriminated union,
        // we need to throw as a base case.
        throw new Error(`cannot recognize format of action record: ${JSON.stringify(actionRecord)}`);
    }, doc);
}
//# sourceMappingURL=apply.js.map